import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { bsiApi } from 'src/api/bsiApi';
import { getPageTitle } from 'src/config/projectConfig';
import { decryptAhsayPassword } from 'src/utils/ahsayDecryption';

const AhsayPortal = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const formRef = useRef<HTMLFormElement>(null);
  const portalUrl = process.env.REACT_APP_AHSAY_PORTAL_URL || '';

  useEffect(() => {
    const fetchAndDecryptCredentials = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch encrypted credentials from API
        const credentials = await bsiApi.getAhsayCredentials();
        setUsername(credentials.ahsayLoginName);

        // Decrypt the password
        const decryptedPassword = await decryptAhsayPassword(
          credentials.ahsayPasswordEncrypted
        );
        setPassword(decryptedPassword);

        setLoading(false);
      } catch (err: any) {
        console.error('Failed to fetch or decrypt Ahsay credentials:', err);
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load Backup credentials. Please try again later.';
        setError(errorMessage);
        setLoading(false);
        toast.error(errorMessage);
      }
    };

    fetchAndDecryptCredentials();
  }, []);

  // Auto-open portal in new tab with auto-login once credentials are ready
  useEffect(() => {
    if (!loading && username && password && portalUrl && formRef.current) {
      const openPortalInNewTab = () => {
        try {
          // Update form values with correct field names from Ahsay portal
          const form = formRef.current;
          if (!form) return;
          
          const usernameInput = form.querySelector('input[name="systemLoginName"]') as HTMLInputElement;
          const passwordInput = form.querySelector('input[name="systemPassword"]') as HTMLInputElement;
          const rememberCheckbox = form.querySelector('input[name="CBRemember"]') as HTMLInputElement;
          
          if (usernameInput) usernameInput.value = username;
          if (passwordInput) passwordInput.value = password;
          if (rememberCheckbox) rememberCheckbox.checked = true;
          
          // Set form target to new tab (_blank)
          form.target = '_blank';
          
          // Submit form - this will open portal in new tab with auto-login
          setTimeout(() => {
            if (formRef.current) {
              formRef.current.submit();
              toast.success('Opening Backup Portal in new tab with automatic login...');
            }
          }, 100);
        } catch (err) {
          console.error('Error opening portal:', err);
          toast.error('Failed to open portal. Please try again.');
          setError('Failed to open portal. Please try again.');
        }
      };
      
      // Small delay to ensure form is ready
      const timer = setTimeout(openPortalInNewTab, 300);
      
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [loading, username, password, portalUrl]);
  
  const handleManualOpen = () => {
    if (formRef.current && username && password) {
      // Update form values
      const form = formRef.current;
      const usernameInput = form.querySelector('input[name="systemLoginName"]') as HTMLInputElement;
      const passwordInput = form.querySelector('input[name="systemPassword"]') as HTMLInputElement;
      const rememberCheckbox = form.querySelector('input[name="CBRemember"]') as HTMLInputElement;
      
      if (usernameInput) usernameInput.value = username;
      if (passwordInput) passwordInput.value = password;
      if (rememberCheckbox) rememberCheckbox.checked = true;
      
      // Set form target to new tab
      form.target = '_blank';
      
      // Submit form - opens in new tab
      form.submit();
      toast.success('Opening Backup Portal in new tab...');
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{getPageTitle('Backup Portal')}</title>
        </Helmet>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="60vh"
          p={3}
        >
          <CircularProgress size={40} />
          <Typography variant="body1" color="textSecondary" mt={2}>
            Loading Backup Portal credentials...
          </Typography>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>{getPageTitle('Backup Portal')}</title>
        </Helmet>
        <Box p={3}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Backup Portal
          </Typography>
          <Box
            mt={3}
            p={3}
            sx={{
              backgroundColor: '#FFF5F5',
              border: '1px solid #FED7D7',
              borderRadius: '8px',
            }}
          >
            <Typography variant="body1" color="error" mb={2}>
              {error}
            </Typography>
            {username && password && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleManualOpen}
              >
                Try Opening Portal Manually
              </Button>
            )}
          </Box>
        </Box>
      </>
    );
  }

  if (!portalUrl) {
    return (
      <>
        <Helmet>
          <title>{getPageTitle('Backup Portal')}</title>
        </Helmet>
        <Box p={3}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Backup Portal
          </Typography>
          <Box
            mt={3}
            p={3}
            sx={{
              backgroundColor: '#FFF5F5',
              border: '1px solid #FED7D7',
              borderRadius: '8px',
            }}
          >
            <Typography variant="body1" color="error">
              Backup Portal URL is not configured. Please set REACT_APP_AHSAY_PORTAL_URL
              environment variable.
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Backup Portal')}</title>
      </Helmet>
      <Box p={3} height="100%">
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Backup Portal
        </Typography>

        {/* Hidden form for auto-login */}
        {/* Using correct Ahsay CBS form field names: systemLoginName, systemPassword, CBRemember */}
        <form
          ref={formRef}
          name="LogonForm"
          method="POST"
          action={portalUrl}
          target="_blank"
          style={{ display: 'none' }}
        >
          <input type="text" name="systemLoginName" defaultValue={username} />
          <input type="password" name="systemPassword" defaultValue={password} />
          <input type="checkbox" name="CBRemember" value="true" defaultChecked />
        </form>

        <Box
          mt={3}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
          p={4}
          sx={{
            backgroundColor: '#F7FAFC',
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
          }}
        >
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Ready to Access Portal
          </Typography>
          <Typography variant="body1" color="textSecondary" align="center" mb={3}>
            Your credentials have been loaded. The Backup Portal will open automatically in a new tab
            <br />
            with automatic login, or you can click the button below to open it manually.
            <br />
            <Typography variant="body2" color="textSecondary" mt={2} sx={{ fontStyle: 'italic' }}>
              Note: If this is your first time, you may need to complete MFA setup.
            </Typography>
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleManualOpen}
            size="large"
            disabled={!username || !password}
          >
            Open Backup Portal in New Tab
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default AhsayPortal;
